/*! @wharfkit/resources v4.0.0-rc7 | BSD-3-Clause | Copyright (c) 2021 Greymass Inc. | https://github.com/wharfkit/js */
import { APIClient, Asset, FetchProvider, Int64, Struct, TimePointSec, UInt128, UInt64 } from "@wharfkit/antelope";
import BN from "bn.js";
import bigDecimal from "js-big-decimal";
//#region \0@oxc-project+runtime@0.147.0/helpers/esm/decorate.js
function __decorate(decorators, target, key, desc) {
	var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
	if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
	else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
	return c > 3 && r && Object.defineProperty(target, key, r), r;
}
//#endregion
//#region src/powerup/abstract.ts
var PowerUpStateResource = class extends Struct {
	constructor(..._args) {
		super(..._args);
		this.default_block_cpu_limit = UInt64.from(2e5);
		this.default_block_net_limit = UInt64.from(1048576e3);
	}
	get allocated() {
		return 1 - Number(this.weight_ratio.dividing(this.target_weight_ratio)) / 100;
	}
	get reserved() {
		return this.utilization.dividing(this.weight);
	}
	get symbol() {
		return this.min_price.symbol;
	}
	utilization_increase(frac) {
		const base = intToBigDecimal(frac);
		const weight = intToBigDecimal(this.weight);
		const multiplier = intToBigDecimal(Math.pow(10, 15));
		return UInt128.from(base.multiply(weight).divide(multiplier, 15).ceil().getValue());
	}
	price_function(utilization) {
		const { weight } = this;
		let price = this.min_price.value;
		const new_exponent = Number(this.exponent) - 1;
		if (new_exponent <= 0) return this.max_price.value;
		else {
			const util_weight = intToBigDecimal(utilization).divide(intToBigDecimal(weight), 18);
			const difference = this.max_price.value - this.min_price.value;
			price += difference * Math.pow(Number(util_weight.getValue()), new_exponent);
		}
		return price;
	}
	price_integral_delta(start_utilization, end_utilization) {
		const coefficient = Asset.fromUnits(this.max_price.units.subtracting(this.min_price.units), this.symbol).value / this.exponent.value;
		const start_u = Number(intToBigDecimal(start_utilization).divide(intToBigDecimal(this.weight), 18).getValue());
		const end_u = Number(intToBigDecimal(end_utilization).divide(intToBigDecimal(this.weight), 18).getValue());
		return this.min_price.value * end_u - this.min_price.value * start_u + coefficient * Math.pow(end_u, this.exponent.value) - coefficient * Math.pow(start_u, this.exponent.value);
	}
	fee(utilization_increase, adjusted_utilization) {
		const { utilization, weight } = this;
		let start_utilization = Int64.from(utilization);
		const end_utilization = start_utilization.adding(utilization_increase);
		let fee = 0;
		if (start_utilization.lt(adjusted_utilization)) {
			const min = Math.min(Number(utilization_increase), Number(adjusted_utilization.subtracting(start_utilization)));
			fee += Number(intToBigDecimal(this.price_function(adjusted_utilization) * min).divide(intToBigDecimal(weight), 18).getValue());
			start_utilization = adjusted_utilization;
		}
		if (start_utilization.lt(end_utilization)) fee += this.price_integral_delta(start_utilization, end_utilization);
		return fee;
	}
	determine_adjusted_utilization(options) {
		const { decay_secs, utilization, utilization_timestamp } = this;
		let { adjusted_utilization } = this;
		if (utilization.lt(adjusted_utilization)) {
			const ts = options && options.timestamp ? options.timestamp : new Date();
			const now = TimePointSec.from(ts).toMilliseconds() / 1e3;
			const utilization_ts = utilization_timestamp.toMilliseconds() / 1e3;
			const diff = adjusted_utilization.subtracting(utilization).toNumber();
			let delta = diff * Math.exp(-(now - utilization_ts) / Number(decay_secs));
			delta = Math.min(Math.max(delta, 0), diff);
			adjusted_utilization = utilization.adding(delta);
		}
		return adjusted_utilization;
	}
};
__decorate([Struct.field("uint8")], PowerUpStateResource.prototype, "version", void 0);
__decorate([Struct.field("int64")], PowerUpStateResource.prototype, "weight", void 0);
__decorate([Struct.field("int64")], PowerUpStateResource.prototype, "weight_ratio", void 0);
__decorate([Struct.field("int64")], PowerUpStateResource.prototype, "assumed_stake_weight", void 0);
__decorate([Struct.field("int64")], PowerUpStateResource.prototype, "initial_weight_ratio", void 0);
__decorate([Struct.field("int64")], PowerUpStateResource.prototype, "target_weight_ratio", void 0);
__decorate([Struct.field("time_point_sec")], PowerUpStateResource.prototype, "initial_timestamp", void 0);
__decorate([Struct.field("time_point_sec")], PowerUpStateResource.prototype, "target_timestamp", void 0);
__decorate([Struct.field("float64")], PowerUpStateResource.prototype, "exponent", void 0);
__decorate([Struct.field("uint32")], PowerUpStateResource.prototype, "decay_secs", void 0);
__decorate([Struct.field("asset")], PowerUpStateResource.prototype, "min_price", void 0);
__decorate([Struct.field("asset")], PowerUpStateResource.prototype, "max_price", void 0);
__decorate([Struct.field("int64")], PowerUpStateResource.prototype, "utilization", void 0);
__decorate([Struct.field("int64")], PowerUpStateResource.prototype, "adjusted_utilization", void 0);
__decorate([Struct.field("time_point_sec")], PowerUpStateResource.prototype, "utilization_timestamp", void 0);
//#endregion
//#region src/powerup/cpu.ts
let PowerUpStateResourceCPU = class PowerUpStateResourceCPU extends PowerUpStateResource {
	constructor(..._args) {
		super(..._args);
		this.per_day = (options) => this.us_per_day(options);
		this.frac = (usage, us) => this.frac_by_us(usage, us);
		this.frac_by_ms = (usage, ms) => this.frac_by_us(usage, ms * 1e3);
		this.price_per = (usage, us = 1e3, options) => this.price_per_us(usage, us, options);
		this.price_per_ms = (usage, ms = 1, options) => this.price_per_us(usage, ms * 1e3, options);
	}
	ms_per_day(options) {
		return this.us_per_day(options) / 1e3;
	}
	us_per_day(options) {
		const limit = options && options.virtual_block_cpu_limit ? options.virtual_block_cpu_limit : this.default_block_cpu_limit;
		return Number(limit) * 2 * 60 * 60 * 24;
	}
	weight_to_us(sample, weight) {
		return UInt128.from(UInt128.from(weight).multiplying(Int64.from(sample)).dividing(BNPrecision, "ceil"));
	}
	us_to_weight(sample, us) {
		return Int64.from(us).multiplying(BNPrecision).dividing(sample, "floor");
	}
	frac_by_us(usage, us) {
		const precision = 15;
		const converted = intToBigDecimal(this.us_to_weight(usage.cpu, us));
		const current = intToBigDecimal(this.weight);
		const multiplier = intToBigDecimal(Math.pow(10, precision));
		const frac = converted.divide(current, precision).multiply(multiplier);
		return Int64.from(frac.getValue());
	}
	price_per_us(usage, us = 1e3, options) {
		const frac = UInt128.from(this.frac(usage, us));
		const utilization_increase = this.utilization_increase(frac);
		const adjusted_utilization = this.determine_adjusted_utilization(options);
		const fee = this.fee(utilization_increase, adjusted_utilization);
		const precision = Math.pow(10, this.max_price.symbol.precision);
		const price = fee * precision;
		const value = Math.ceil(price) / precision;
		const asset = Asset.fromFloat(fee, this.symbol);
		if (price < 1) throw new Error(`Price (${String(asset)}) for requested CPU amount (${us}us) below required precision, increase requested amount.`);
		if (options && options.min_payment && options.min_payment.units.gt(asset.units)) throw new Error(`Price (${String(asset)}) for requested CPU amount (${us}us) below minimum required payment (${String(options.min_payment)}), increase requested CPU amount.`);
		return value;
	}
};
PowerUpStateResourceCPU = __decorate([Struct.type("powerupstateresourcecpu")], PowerUpStateResourceCPU);
//#endregion
//#region src/powerup/net.ts
let PowerUpStateResourceNET = class PowerUpStateResourceNET extends PowerUpStateResource {
	constructor(..._args) {
		super(..._args);
		this.per_day = (options) => this.bytes_per_day(options);
		this.frac = (usage, bytes) => this.frac_by_bytes(usage, bytes);
		this.frac_by_kb = (usage, kilobytes) => this.frac_by_bytes(usage, kilobytes * 1e3);
		this.price_per = (usage, bytes = 1e3, options) => this.price_per_byte(usage, bytes, options);
		this.price_per_kb = (usage, kilobytes = 1, options) => this.price_per_byte(usage, kilobytes * 1e3, options);
	}
	kb_per_day(options) {
		return this.bytes_per_day(options) / 1e3;
	}
	bytes_per_day(options) {
		const limit = options && options.virtual_block_net_limit ? options.virtual_block_net_limit : this.default_block_net_limit;
		return Number(limit) * 2 * 60 * 60 * 24;
	}
	weight_to_bytes(sample, weight) {
		return UInt128.from(UInt128.from(weight).multiplying(Int64.from(sample)).dividing(BNPrecision, "ceil"));
	}
	bytes_to_weight(sample, bytes) {
		return Int64.from(bytes).multiplying(BNPrecision).dividing(Int64.from(sample), "floor");
	}
	frac_by_bytes(usage, bytes) {
		const precision = 15;
		const converted = intToBigDecimal(this.bytes_to_weight(usage.net, bytes));
		const current = intToBigDecimal(this.weight);
		const multiplier = intToBigDecimal(Math.pow(10, precision));
		const frac = converted.divide(current, precision).multiply(multiplier);
		return Int64.from(frac.getValue());
	}
	price_per_byte(usage, bytes = 1e3, options) {
		const frac = UInt128.from(this.frac(usage, bytes));
		const utilization_increase = this.utilization_increase(frac);
		const adjusted_utilization = this.determine_adjusted_utilization(options);
		const fee = this.fee(utilization_increase, adjusted_utilization);
		const precision = Math.pow(10, this.max_price.symbol.precision);
		return Math.ceil(fee * precision) / precision;
	}
};
PowerUpStateResourceNET = __decorate([Struct.type("powerupstateresourcenet")], PowerUpStateResourceNET);
//#endregion
//#region src/powerup.ts
let PowerUpState = class PowerUpState extends Struct {};
__decorate([Struct.field("uint8")], PowerUpState.prototype, "version", void 0);
__decorate([Struct.field(PowerUpStateResourceNET)], PowerUpState.prototype, "net", void 0);
__decorate([Struct.field(PowerUpStateResourceCPU)], PowerUpState.prototype, "cpu", void 0);
__decorate([Struct.field("uint32")], PowerUpState.prototype, "powerup_days", void 0);
__decorate([Struct.field("asset")], PowerUpState.prototype, "min_powerup_fee", void 0);
PowerUpState = __decorate([Struct.type("powerupstate")], PowerUpState);
var PowerUpAPI = class {
	constructor(parent) {
		this.parent = parent;
	}
	async get_state() {
		return (await this.parent.api.v1.chain.get_table_rows({
			code: "eosio",
			scope: "",
			table: "powup.state",
			type: PowerUpState
		})).rows[0];
	}
};
//#endregion
//#region src/ram.ts
let Connector = class Connector extends Struct {};
__decorate([Struct.field("asset")], Connector.prototype, "balance", void 0);
__decorate([Struct.field("float64")], Connector.prototype, "weight", void 0);
Connector = __decorate([Struct.type("connector")], Connector);
let ExchangeState = class ExchangeState extends Struct {};
__decorate([Struct.field("asset")], ExchangeState.prototype, "supply", void 0);
__decorate([Struct.field(Connector)], ExchangeState.prototype, "base", void 0);
__decorate([Struct.field(Connector)], ExchangeState.prototype, "quote", void 0);
ExchangeState = __decorate([Struct.type("exchange_state")], ExchangeState);
let RAMState = class RAMState extends ExchangeState {
	price_per(bytes) {
		const base = this.base.balance.units;
		const quote = this.quote.balance.units;
		return Asset.fromUnits(this.get_input(base, quote, Int64.from(bytes)), this.quote.balance.symbol);
	}
	price_per_kb(kilobytes) {
		return this.price_per(kilobytes * 1e3);
	}
	get_input(base, quote, value) {
		return quote.multiplying(value).dividing(base.subtracting(value), "ceil");
	}
};
RAMState = __decorate([Struct.type("ramstate")], RAMState);
var RAMAPI = class {
	constructor(parent) {
		this.parent = parent;
	}
	async get_state() {
		return (await this.parent.api.v1.chain.get_table_rows({
			code: "eosio",
			scope: "eosio",
			table: "rammarket",
			type: RAMState
		})).rows[0];
	}
};
//#endregion
//#region src/rex.ts
let REXState = class REXState extends Struct {
	get reserved() {
		return Number(this.total_lent.units) / Number(this.total_lendable.units);
	}
	get symbol() {
		return this.total_lent.symbol;
	}
	get precision() {
		return this.total_lent.symbol.precision;
	}
	get value() {
		const lent = intToBigDecimal(this.total_lent.units);
		const unlent = intToBigDecimal(this.total_unlent.units);
		const rex = intToBigDecimal(this.total_rex.units);
		return Number(lent.add(unlent).divide(rex, 18).getValue());
	}
	exchange(amount) {
		const value = intToBigDecimal(amount.units);
		const lendable = intToBigDecimal(this.total_lendable.units);
		const rex = intToBigDecimal(this.total_rex.units);
		const tokens = value.multiply(lendable).divide(rex, this.precision);
		return Asset.fromUnits(Number(tokens.getValue()), this.symbol);
	}
	cpu_price_per_ms(sample, ms = 1) {
		return this.cpu_price_per_us(sample, ms * 1e3);
	}
	cpu_price_per_us(sample, us = 1e3) {
		return this.price_per(sample, us, sample.cpu);
	}
	net_price_per_kb(sample, kilobytes = 1) {
		return this.net_price_per_byte(sample, kilobytes * 1e3);
	}
	net_price_per_byte(sample, bytes = 1e3) {
		return this.price_per(sample, bytes, sample.net);
	}
	price_per(sample, unit = 1e3, usage = sample.cpu) {
		const tokens = Asset.fromUnits(1e4, this.symbol);
		const unitPrice = Number(tokens.units) / (this.total_rent.value / this.total_unlent.value) * (Number(usage) / Number(BNPrecision));
		return Number(tokens.units) / unitPrice * unit / Math.pow(10, this.precision);
	}
};
__decorate([Struct.field("uint8")], REXState.prototype, "version", void 0);
__decorate([Struct.field("asset")], REXState.prototype, "total_lent", void 0);
__decorate([Struct.field("asset")], REXState.prototype, "total_unlent", void 0);
__decorate([Struct.field("asset")], REXState.prototype, "total_rent", void 0);
__decorate([Struct.field("asset")], REXState.prototype, "total_lendable", void 0);
__decorate([Struct.field("asset")], REXState.prototype, "total_rex", void 0);
__decorate([Struct.field("asset")], REXState.prototype, "namebid_proceeds", void 0);
__decorate([Struct.field("uint64")], REXState.prototype, "loan_num", void 0);
REXState = __decorate([Struct.type("rexstate")], REXState);
var REXAPI = class {
	constructor(parent) {
		this.parent = parent;
	}
	async get_state() {
		return (await this.parent.api.v1.chain.get_table_rows({
			code: "eosio",
			scope: "eosio",
			table: "rexpool",
			type: REXState
		})).rows[0];
	}
};
//#endregion
//#region src/index.ts
const BNPrecision = new BN(1e8);
var Resources = class {
	constructor(options) {
		this.sampleAccount = "greymassfuel";
		this.symbol = "4,EOS";
		this.v1 = {
			powerup: new PowerUpAPI(this),
			ram: new RAMAPI(this),
			rex: new REXAPI(this)
		};
		if (options.sampleAccount) this.sampleAccount = options.sampleAccount;
		if (options.symbol) this.symbol = options.symbol;
		if (options.api) this.api = options.api;
		else if (options.url) this.api = new APIClient({ provider: new FetchProvider(options.url, options) });
		else throw new Error("Missing url or api client");
	}
	async getSampledUsage() {
		const account = await this.api.v1.chain.get_account(this.sampleAccount);
		const us = UInt128.from(account.cpu_limit.max.value.mul(BNPrecision));
		const byte = UInt128.from(account.net_limit.max.value.mul(BNPrecision));
		const cpu_weight = UInt128.from(account.cpu_weight.value);
		const net_weight = UInt128.from(account.net_weight.value);
		return {
			account,
			cpu: divCeil(us.value, cpu_weight.value),
			net: divCeil(byte.value, net_weight.value)
		};
	}
};
Resources.__className = "Resources";
function divCeil(num, den) {
	let v = num.div(den);
	const zero = new BN(0);
	const one = new BN(1);
	if (num.mod(den).gt(zero) && v.gt(one)) v = v.sub(one);
	return UInt128.from(String(v));
}
function intToBigDecimal(value) {
	return new bigDecimal(String(value));
}
//#endregion
export { BNPrecision, Connector, ExchangeState, PowerUpAPI, PowerUpState, RAMAPI, RAMState, REXAPI, REXState, Resources, intToBigDecimal };

//# sourceMappingURL=wharfkit-resources.m.js.map